import { Link } from "react-router-dom";
import { ArrowLeft, Trophy } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-slate-900 py-6">
        <div className="max-w-4xl mx-auto px-6 flex items-center gap-4">
          <Link to="/">
            <Button variant="ghost" size="sm" className="text-white/70 hover:text-white hover:bg-white/10 rounded-sm gap-1">
              <ArrowLeft className="h-4 w-4" /> Torna al sito
            </Button>
          </Link>
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 bg-[#c8e414] rounded-full flex items-center justify-center">
              <Trophy className="h-3.5 w-3.5 text-slate-900" strokeWidth={2.5} />
            </div>
            <span className="font-heading text-lg font-bold text-white uppercase tracking-tight">Tabellone Facile</span>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight text-slate-900 mb-2">
          Condizioni d'Uso
        </h1>
        <p className="text-sm text-slate-500 mb-10">Ultimo aggiornamento: Aprile 2026</p>

        <div className="prose prose-slate max-w-none space-y-8 text-sm leading-relaxed text-slate-700">

          <section>
            <h2 className="font-heading text-lg font-bold uppercase tracking-wide text-slate-900 mb-3">1. Premesse e definizioni</h2>
            <p>
              Le presenti Condizioni d'Uso (di seguito "Condizioni") regolano l'accesso e l'utilizzo della piattaforma 
              <strong> Tabellone Facile</strong> (di seguito "Piattaforma" o "Servizio"), accessibile tramite il sito web 
              e l'applicazione web, destinata alla gestione e organizzazione di tornei di tennis.
            </p>
            <div className="bg-white border border-slate-200 rounded-sm p-4 mt-3 space-y-2">
              <p><strong>"Titolare"</strong>: il soggetto giuridico che gestisce e fornisce il Servizio Tabellone Facile.</p>
              <p><strong>"Utente"</strong>: qualsiasi persona fisica o giuridica che accede, si registra o utilizza la Piattaforma.</p>
              <p><strong>"Organizzatore"</strong>: l'Utente che crea e gestisce tornei tramite la Piattaforma.</p>
              <p><strong>"Giocatore"</strong>: il soggetto i cui dati vengono inseriti dall'Organizzatore per la partecipazione ai tornei.</p>
              <p><strong>"Account"</strong>: il profilo personale dell'Utente creato al momento della registrazione.</p>
              <p><strong>"Abbonamento"</strong>: il piano a pagamento sottoscritto dall'Utente per accedere alle funzionalita della Piattaforma.</p>
            </div>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold uppercase tracking-wide text-slate-900 mb-3">2. Accettazione delle condizioni</h2>
            <p>
              L'accesso e l'utilizzo della Piattaforma comportano l'accettazione integrale delle presenti Condizioni. 
              Se l'Utente non accetta le Condizioni, e tenuto a non utilizzare il Servizio.
            </p>
            <p className="mt-2">
              Il Titolare si riserva il diritto di modificare le presenti Condizioni in qualsiasi momento. Le modifiche saranno 
              comunicate tramite pubblicazione sulla Piattaforma e, per le modifiche sostanziali, tramite email all'indirizzo 
              fornito dall'Utente. L'uso continuato del Servizio dopo la pubblicazione delle modifiche costituisce accettazione 
              delle stesse.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold uppercase tracking-wide text-slate-900 mb-3">3. Registrazione e account</h2>
            <div className="space-y-3">
              <div className="bg-white border border-slate-200 rounded-sm p-4">
                <h3 className="font-bold text-slate-900 text-sm mb-2">3.1 Requisiti di registrazione</h3>
                <ul className="list-disc list-inside space-y-1 text-slate-600">
                  <li>L'Utente deve avere almeno 18 anni di eta o agire con il consenso di un tutore legale.</li>
                  <li>La registrazione puo avvenire tramite email e password, Google OAuth o Facebook OAuth.</li>
                  <li>L'Utente e tenuto a fornire informazioni veritiere, accurate e aggiornate.</li>
                </ul>
              </div>
              <div className="bg-white border border-slate-200 rounded-sm p-4">
                <h3 className="font-bold text-slate-900 text-sm mb-2">3.2 Sicurezza dell'Account</h3>
                <ul className="list-disc list-inside space-y-1 text-slate-600">
                  <li>L'Utente e responsabile della riservatezza delle proprie credenziali di accesso.</li>
                  <li>Qualsiasi attivita effettuata tramite l'Account dell'Utente e di sua responsabilita.</li>
                  <li>L'Utente deve notificare immediatamente al Titolare qualsiasi accesso non autorizzato.</li>
                </ul>
              </div>
              <div className="bg-white border border-slate-200 rounded-sm p-4">
                <h3 className="font-bold text-slate-900 text-sm mb-2">3.3 Sospensione e chiusura dell'Account</h3>
                <p className="text-slate-600">
                  Il Titolare si riserva il diritto di sospendere o chiudere l'Account dell'Utente in caso di violazione 
                  delle presenti Condizioni, utilizzo fraudolento del Servizio, o mancato pagamento degli abbonamenti dovuti.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold uppercase tracking-wide text-slate-900 mb-3">4. Descrizione del servizio</h2>
            <p>La Piattaforma offre le seguenti funzionalita principali:</p>
            <div className="grid sm:grid-cols-2 gap-2 mt-3">
              {[
                "Creazione e gestione di tornei di tennis",
                "Generazione automatica di tabelloni e calendari",
                "Schedulazione intelligente delle partite su piu campi",
                "Gestione dei giocatori e delle categorie",
                "Notifiche email per partite, risultati e scadenze",
                "Export PDF di tabelloni e calendari",
                "Gestione delle informazioni del circolo",
                "Dashboard di amministrazione"
              ].map((feat, i) => (
                <div key={feat} className="flex items-start gap-2 bg-white border border-slate-200 rounded-sm p-3">
                  <span className="text-green-700 font-bold text-xs mt-0.5">{i + 1}.</span>
                  <span className="text-sm">{feat}</span>
                </div>
              ))}
            </div>
            <p className="mt-3">
              Il Titolare si riserva il diritto di modificare, aggiornare o interrompere funzionalita del Servizio 
              in qualsiasi momento, con ragionevole preavviso agli Utenti con Abbonamento attivo.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold uppercase tracking-wide text-slate-900 mb-3">5. Periodo di prova e abbonamenti</h2>
            <div className="space-y-3">
              <div className="bg-white border border-slate-200 rounded-sm p-4">
                <h3 className="font-bold text-slate-900 text-sm mb-2">5.1 Periodo di prova gratuito</h3>
                <ul className="list-disc list-inside space-y-1 text-slate-600">
                  <li>Ogni nuovo Utente ha diritto a un periodo di prova gratuito di <strong>14 giorni</strong>.</li>
                  <li>Durante il periodo di prova, l'Utente puo creare al massimo <strong>1 torneo</strong> con un massimo di <strong>4 categorie</strong>.</li>
                  <li>Il periodo di prova non richiede l'inserimento di dati di pagamento.</li>
                  <li>Al termine del periodo di prova, l'accesso alle funzionalita sara limitato fino alla sottoscrizione di un Abbonamento.</li>
                </ul>
              </div>
              <div className="bg-white border border-slate-200 rounded-sm p-4">
                <h3 className="font-bold text-slate-900 text-sm mb-2">5.2 Piani di abbonamento</h3>
                <p className="text-slate-600 mb-2">La Piattaforma offre i seguenti piani di abbonamento:</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs border border-slate-100">
                    <thead><tr className="bg-slate-50"><th className="text-left p-2 font-bold">Piano</th><th className="text-left p-2 font-bold">Durata</th><th className="text-left p-2 font-bold">Rinnovo</th></tr></thead>
                    <tbody className="text-slate-600">
                      <tr className="border-t"><td className="p-2">Mensile</td><td className="p-2">1 mese</td><td className="p-2">Automatico</td></tr>
                      <tr className="border-t"><td className="p-2">Trimestrale</td><td className="p-2">3 mesi</td><td className="p-2">Automatico</td></tr>
                      <tr className="border-t"><td className="p-2">Semestrale</td><td className="p-2">6 mesi</td><td className="p-2">Automatico</td></tr>
                      <tr className="border-t"><td className="p-2">Novemestrale</td><td className="p-2">9 mesi</td><td className="p-2">Automatico</td></tr>
                      <tr className="border-t"><td className="p-2">Annuale</td><td className="p-2">12 mesi</td><td className="p-2">Automatico</td></tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="bg-white border border-slate-200 rounded-sm p-4">
                <h3 className="font-bold text-slate-900 text-sm mb-2">5.3 Pagamenti</h3>
                <ul className="list-disc list-inside space-y-1 text-slate-600">
                  <li>I pagamenti vengono elaborati tramite <strong>Stripe</strong> e/o <strong>PayPal</strong>.</li>
                  <li>I prezzi sono espressi in Euro (EUR) e sono comprensivi di IVA ove applicabile.</li>
                  <li>Il Titolare si riserva il diritto di modificare i prezzi con un preavviso di almeno 30 giorni.</li>
                  <li>Il Titolare non conserva direttamente i dati delle carte di credito, che vengono gestiti dai processori di pagamento certificati PCI-DSS.</li>
                </ul>
              </div>
              <div className="bg-white border border-slate-200 rounded-sm p-4">
                <h3 className="font-bold text-slate-900 text-sm mb-2">5.4 Rinnovo e disdetta</h3>
                <ul className="list-disc list-inside space-y-1 text-slate-600">
                  <li>Gli abbonamenti si rinnovano automaticamente alla scadenza del periodo sottoscritto.</li>
                  <li>L'Utente puo disdire l'Abbonamento in qualsiasi momento tramite la sezione "Abbonamento" del proprio profilo.</li>
                  <li>La disdetta ha effetto al termine del periodo gia pagato. Non sono previsti rimborsi per periodi parziali.</li>
                  <li>L'Utente ricevera notifiche email di promemoria scadenza a 30, 15, 7 e 1 giorno prima del rinnovo.</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold uppercase tracking-wide text-slate-900 mb-3">6. Uso accettabile</h2>
            <p>L'Utente si impegna a utilizzare la Piattaforma nel rispetto delle presenti Condizioni e della normativa vigente. In particolare, e vietato:</p>
            <div className="bg-red-50 border border-red-200 rounded-sm p-4 mt-3">
              <ul className="list-disc list-inside space-y-2 text-red-800 text-sm">
                <li>Utilizzare il Servizio per finalita illegali, fraudolente o non autorizzate.</li>
                <li>Inserire dati personali di terzi senza il loro consenso o senza idonea base giuridica.</li>
                <li>Tentare di accedere a dati o funzionalita non autorizzate, o di compromettere la sicurezza della Piattaforma.</li>
                <li>Utilizzare strumenti automatizzati (bot, scraper) per accedere al Servizio senza autorizzazione scritta.</li>
                <li>Condividere le proprie credenziali di accesso con terzi o creare account multipli per eludere i limiti del piano.</li>
                <li>Caricare contenuti offensivi, diffamatori, o che violino diritti di proprieta intellettuale di terzi.</li>
                <li>Interferire con il funzionamento della Piattaforma o con l'esperienza di altri Utenti.</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold uppercase tracking-wide text-slate-900 mb-3">7. Proprieta intellettuale</h2>
            <p>
              Tutti i contenuti della Piattaforma, inclusi ma non limitati a software, design, loghi, testi, grafica e 
              documentazione, sono di proprieta esclusiva del Titolare o dei suoi licenzianti e sono protetti dalle leggi 
              italiane e internazionali sulla proprieta intellettuale.
            </p>
            <p className="mt-2">
              L'Utente conserva la titolarita dei dati inseriti nella Piattaforma (informazioni sui tornei, giocatori, risultati). 
              L'Utente concede al Titolare una licenza limitata, non esclusiva e revocabile per l'elaborazione di tali dati 
              ai soli fini dell'erogazione del Servizio.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold uppercase tracking-wide text-slate-900 mb-3">8. Protezione dei dati personali</h2>
            <p>
              Il trattamento dei dati personali e regolato dalla nostra{" "}
              <Link to="/privacy-policy" className="text-green-700 font-medium underline underline-offset-2">Informativa sulla Privacy</Link>, 
              che costituisce parte integrante delle presenti Condizioni.
            </p>
            <p className="mt-2">
              L'Organizzatore, in qualita di titolare autonomo del trattamento dei dati dei Giocatori inseriti nella Piattaforma, 
              e responsabile di raccogliere il consenso necessario e di garantire la conformita al <strong>Regolamento (UE) 2016/679</strong> (GDPR) 
              e al <strong>D.Lgs. 196/2003</strong> (Codice Privacy) per il trattamento dei dati dei Giocatori.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold uppercase tracking-wide text-slate-900 mb-3">9. Limitazione di responsabilita</h2>
            <div className="space-y-3">
              <div className="bg-amber-50 border border-amber-200 rounded-sm p-4">
                <h3 className="font-bold text-amber-900 text-sm mb-2">9.1 Disponibilita del Servizio</h3>
                <p className="text-amber-800 text-sm">
                  Il Titolare si impegna a garantire la massima disponibilita del Servizio, ma non garantisce che la Piattaforma 
                  sia disponibile in modo ininterrotto o priva di errori. Il Servizio e fornito "cosi com'e" (<em>as is</em>) e 
                  "come disponibile" (<em>as available</em>).
                </p>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-sm p-4">
                <h3 className="font-bold text-amber-900 text-sm mb-2">9.2 Esclusioni di responsabilita</h3>
                <p className="text-amber-800 text-sm mb-2">Nei limiti consentiti dalla legge applicabile, il Titolare non e responsabile per:</p>
                <ul className="list-disc list-inside space-y-1 text-amber-700 text-sm">
                  <li>Danni indiretti, incidentali, speciali o consequenziali derivanti dall'uso del Servizio.</li>
                  <li>Perdita di dati causata da circostanze al di fuori del ragionevole controllo del Titolare.</li>
                  <li>Interruzioni del Servizio dovute a manutenzione programmata o cause di forza maggiore.</li>
                  <li>Azioni o omissioni di fornitori terzi (Stripe, PayPal, SendGrid, Google, Facebook).</li>
                  <li>Contenuti, dati o informazioni inseriti dagli Utenti nella Piattaforma.</li>
                  <li>Decisioni organizzative prese dall'Organizzatore sulla base dei dati generati dalla Piattaforma.</li>
                </ul>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-sm p-4">
                <h3 className="font-bold text-amber-900 text-sm mb-2">9.3 Limite massimo di responsabilita</h3>
                <p className="text-amber-800 text-sm">
                  In ogni caso, la responsabilita complessiva del Titolare nei confronti dell'Utente non potra eccedere 
                  l'importo totale corrisposto dall'Utente per l'Abbonamento nei <strong>12 mesi</strong> precedenti l'evento 
                  che ha dato origine alla responsabilita.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold uppercase tracking-wide text-slate-900 mb-3">10. Indennizzo</h2>
            <p>
              L'Utente si impegna a manlevare e tenere indenne il Titolare da qualsiasi pretesa, danno, costo o spesa 
              (incluse le spese legali ragionevoli) derivanti dalla violazione delle presenti Condizioni, dall'uso improprio 
              del Servizio, o dalla violazione di diritti di terzi da parte dell'Utente.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold uppercase tracking-wide text-slate-900 mb-3">11. Cancellazione dell'account e dei dati</h2>
            <div className="space-y-3">
              <div className="bg-white border border-slate-200 rounded-sm p-4">
                <h3 className="font-bold text-slate-900 text-sm mb-2">11.1 Cancellazione da parte dell'Utente</h3>
                <ul className="list-disc list-inside space-y-1 text-slate-600">
                  <li>L'Utente puo richiedere la cancellazione del proprio Account in qualsiasi momento scrivendo a <strong>privacy@tabellonefacile.net</strong>.</li>
                  <li>La cancellazione comporta l'eliminazione definitiva dell'Account e di tutti i dati associati entro 30 giorni dalla richiesta.</li>
                  <li>I dati di fatturazione saranno conservati per il periodo previsto dalla normativa fiscale (10 anni).</li>
                </ul>
              </div>
              <div className="bg-white border border-slate-200 rounded-sm p-4">
                <h3 className="font-bold text-slate-900 text-sm mb-2">11.2 Cancellazione da parte del Titolare</h3>
                <ul className="list-disc list-inside space-y-1 text-slate-600">
                  <li>Il Titolare puo cancellare l'Account dell'Utente in caso di grave violazione delle presenti Condizioni.</li>
                  <li>In caso di cancellazione per violazione, l'Utente non ha diritto ad alcun rimborso per l'Abbonamento in corso.</li>
                </ul>
              </div>
              <div className="bg-white border border-slate-200 rounded-sm p-4">
                <h3 className="font-bold text-slate-900 text-sm mb-2">11.3 Effetti della cancellazione</h3>
                <p className="text-slate-600">
                  Alla cancellazione dell'Account, tutti i tornei, i calendari, i tabelloni e i dati dei giocatori associati 
                  all'Utente saranno eliminati definitivamente e non saranno recuperabili.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold uppercase tracking-wide text-slate-900 mb-3">12. Forza maggiore</h2>
            <p>
              Il Titolare non sara responsabile per ritardi o inadempimenti dovuti a cause di forza maggiore, inclusi ma non 
              limitati a: disastri naturali, pandemie, interruzioni di servizi di terze parti, attacchi informatici, 
              provvedimenti governativi, o qualsiasi altra circostanza al di fuori del ragionevole controllo del Titolare.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold uppercase tracking-wide text-slate-900 mb-3">13. Legge applicabile e foro competente</h2>
            <p>
              Le presenti Condizioni sono regolate dalla <strong>legge italiana</strong>. Per qualsiasi controversia derivante 
              dall'interpretazione, esecuzione o risoluzione delle presenti Condizioni, sara competente in via esclusiva il 
              <strong> Foro del luogo di residenza o domicilio del consumatore</strong>, se l'Utente agisce in qualita di consumatore 
              ai sensi del D.Lgs. 206/2005 (Codice del Consumo).
            </p>
            <p className="mt-2">
              Qualora l'Utente non sia qualificabile come consumatore, sara competente in via esclusiva il Foro della sede legale del Titolare.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold uppercase tracking-wide text-slate-900 mb-3">14. Risoluzione alternativa delle controversie</h2>
            <p>
              Ai sensi dell'art. 14 del Regolamento (UE) 524/2013, si informa l'Utente consumatore che la Commissione Europea 
              mette a disposizione una piattaforma per la risoluzione online delle controversie (piattaforma ODR), accessibile 
              all'indirizzo: <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer" className="text-green-700 underline underline-offset-2">https://ec.europa.eu/consumers/odr</a>.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold uppercase tracking-wide text-slate-900 mb-3">15. Disposizioni finali</h2>
            <ul className="list-disc list-inside space-y-2">
              <li>
                <strong>Separabilita:</strong> Se una disposizione delle presenti Condizioni dovesse risultare nulla o inapplicabile, 
                le restanti disposizioni rimarranno pienamente valide ed efficaci.
              </li>
              <li>
                <strong>Rinuncia:</strong> Il mancato esercizio da parte del Titolare di un diritto previsto dalle presenti Condizioni 
                non costituisce rinuncia a tale diritto.
              </li>
              <li>
                <strong>Intero accordo:</strong> Le presenti Condizioni, unitamente all'{" "}
                <Link to="/privacy-policy" className="text-green-700 font-medium underline underline-offset-2">Informativa sulla Privacy</Link> e alla{" "}
                <Link to="/cookie-policy" className="text-green-700 font-medium underline underline-offset-2">Cookie Policy</Link>, 
                costituiscono l'intero accordo tra l'Utente e il Titolare.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold uppercase tracking-wide text-slate-900 mb-3">16. Contatti</h2>
            <p>
              Per qualsiasi domanda relativa alle presenti Condizioni, e possibile contattarci all'indirizzo: <strong>info@tabellonefacile.net</strong>
            </p>
          </section>
        </div>
      </main>

      <footer className="bg-slate-900 py-8 mt-12">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-slate-400 text-xs">Tabellone Facile - Gestione intelligente dei tornei di tennis</p>
        </div>
      </footer>
    </div>
  );
}
